/**
 * กล่องเฉลยแบบใหม่ (D20 · mockup docs/ui-book-theme.html ปุ่ม "กล่องเฉลย (หน้าทำข้อสอบ)")
 * ใช้ทั้งหน้าทำข้อสอบ (โหมดฝึก หลังตอบ) และหน้าเฉลยทีละข้อ — โครงเดียวกันบนลงล่าง:
 *
 *   1. หัวกล่อง  ถูก = พื้นเขียวอ่อน ไอคอนเครื่องหมายถูก "ถูกต้อง" · ผิด = พื้นเหลืองอ่อน ไอคอนหลอดไฟ
 *   2. (ผิดเท่านั้น) "คำตอบที่ถูก" + ป้ายเขียวข้อความตัวเลือกที่ถูก — หาจาก **choice id** ไม่ใช่ index
 *   3. คำอธิบาย 19px/1.9 · คำใน `key_terms` ถูกครอบ <mark> เฉพาะการปรากฏครั้งแรกของแต่ละคำ
 *      ★ escape อักขระพิเศษก่อนหา ("ก.ค. (2)" ต้องไม่พัง) · คำที่ไม่มีในคำอธิบาย = ไม่ครอบอะไร
 *   4. กล่อง "จำง่าย ๆ" เฉพาะข้อที่มี `tip` — ไม่มี = ไม่มี element นี้ใน DOM เลย
 *   5. บรรทัดอ้างอิง (ไอคอนหนังสือ · เส้นประคั่น · ชื่อหนังสือตัวหนา) เฉพาะข้อที่มี source
 *
 * ★ CSS ของกล่องอยู่ใน styles/answerbox.css และ **พึ่ง token ของตัวเอง** ทั้งหมด จึงหน้าตาเดียวกัน
 *   ทั้งบนหน้าทำข้อสอบ (ธีมเดิม) และหน้าเฉลย (ธีมหนังสือเรียน) · สีวิชาจาก `colorKey`
 */
import type { ReactNode } from "react";

import { translate as t } from "@/i18n";
import { colorClass } from "@/lib/book";
import { markTerms } from "@/lib/markTerms";
import { renderMath } from "@/lib/math";

export interface AnswerBoxProps {
  correct: boolean;
  /** ข้อความของตัวเลือกที่ถูก (หาจาก id แล้ว) — แสดงเฉพาะตอนตอบผิด */
  correctText: string | null;
  explanation: string | null | undefined;
  tip?: string | null | undefined;
  keyTerms?: string[] | null | undefined;
  sourceDisplay?: string | null | undefined;
  sourceBook?: string | null | undefined;
  colorKey?: string | null | undefined;
  /** ส่วนท้ายเพิ่มเติม (เช่น รอยเขียนของเด็ก) — อยู่นอกโครง 1–5 */
  children?: ReactNode;
}

function SourceLine({
  display,
  book,
}: {
  display: string;
  book: string | null | undefined;
}): JSX.Element {
  // ชื่อหนังสือตัวหนา — ถ้า display ขึ้นต้นด้วยชื่อหนังสือ ตัดออกแล้วต่อท้าย ไม่งั้นแสดงทั้งบรรทัด
  const rest =
    book && display.startsWith(book) ? display.slice(book.length) : null;
  return (
    <div className="src" data-testid="source">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
        <path d="M4 20.5V5.5M8 7h8M8 10.5h6" />
      </svg>
      <span>
        {t("quiz.source")}{" "}
        {rest !== null ? (
          <>
            <b>{book}</b>
            {rest}
          </>
        ) : (
          display
        )}
      </span>
    </div>
  );
}

export function AnswerBox({
  correct,
  correctText,
  explanation,
  tip,
  keyTerms,
  sourceDisplay,
  sourceBook,
  colorKey,
  children,
}: AnswerBoxProps): JSX.Element {
  const body = explanation ?? "";
  return (
    <div
      className={`fb exp ${correct ? "ok" : "no"} ${colorClass(colorKey)}`}
      data-testid="answer-box"
    >
      <div className="head fb-top">
        <span className="ic" aria-hidden="true">
          {correct ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2e9a5b"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c98a12"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.7.6 1 1.3 1 2.1h5c0-.8.3-1.5 1-2.1A6 6 0 0 0 12 3z" />
            </svg>
          )}
        </span>
        <b>{correct ? t("quiz.correct") : t("quiz.notYet")}</b>
      </div>
      <div className="body">
        {!correct && correctText ? (
          <div className="ans" data-testid="correct-answer">
            {t("quiz.correctAnswer")} <b>{renderMath(correctText)}</b>
          </div>
        ) : null}
        <div className="fb-body">
          {/* D23 — คำอธิบายรักษาการขึ้นบรรทัดใหม่ (white-space: pre-line ใน answerbox.css)
              จึงเขียน "วิธีทำ" ทีละบรรทัดปิดท้าย "ตอบ …" ได้ · สูตรใน $...$ เรนเดอร์ด้วย KaTeX */}
          <p data-testid="explanation">
            {keyTerms?.length
              ? markTerms(body, keyTerms, renderMath)
              : renderMath(body)}
          </p>
        </div>
        {tip ? (
          <div className="tip" data-testid="tip">
            <span className="t">{t("quiz.tipTitle")}</span>
            <span>{renderMath(tip)}</span>
          </div>
        ) : null}
        {sourceDisplay ? (
          <SourceLine display={sourceDisplay} book={sourceBook} />
        ) : null}
      </div>
      {children}
    </div>
  );
}
